import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef } from "react";
import { Animated, Easing, Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients } from "@/lib/theme";

const STRIPE_WIDTH = 340;
const USE_NATIVE_DRIVER = Platform.OS !== "web";

function ThinkingDots() {
    const d1 = useRef(new Animated.Value(0)).current;
    const d2 = useRef(new Animated.Value(0)).current;
    const d3 = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const loop = (val: Animated.Value, delay: number) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(val, {
                        toValue: 1,
                        duration: 480,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: USE_NATIVE_DRIVER
                    }),
                    Animated.timing(val, {
                        toValue: 0,
                        duration: 480,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: USE_NATIVE_DRIVER
                    })
                ])
            );
        const a = loop(d1, 0);
        const b = loop(d2, 200);
        const c = loop(d3, 400);
        a.start();
        b.start();
        c.start();
        return () => {
            a.stop();
            b.stop();
            c.stop();
        };
    }, [d1, d2, d3]);

    const dotStyle = (val: Animated.Value) => ({
        opacity: val.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] }),
        transform: [{ translateY: val.interpolate({ inputRange: [0, 1], outputRange: [0, -3] }) }]
    });

    return (
        <View style={styles.dotsRow}>
            <Animated.View style={[styles.dot, dotStyle(d1)]} />
            <Animated.View style={[styles.dot, dotStyle(d2)]} />
            <Animated.View style={[styles.dot, dotStyle(d3)]} />
        </View>
    );
}

interface Props {
    fieldLabel: string;
    reduced: boolean;
}

/**
 * 340-wide pill: AI orb | "Transcribing" + dots | "Adding to {field}".
 * Pure presentation. The field label is captured at recording start time
 * and passed through the recorder's state — never reads activeField, so
 * focus moving during STT doesn't reroute the displayed label.
 */
export function ThinkingStripe({ fieldLabel, reduced }: Props) {
    const scale = useRef(new Animated.Value(reduced ? 1 : 0.6)).current;
    const opacity = useRef(new Animated.Value(reduced ? 1 : 0)).current;

    useEffect(() => {
        if (reduced) {
            scale.setValue(1);
            opacity.setValue(1);
            return;
        }
        Animated.parallel([
            Animated.spring(scale, {
                toValue: 1,
                friction: 9,
                tension: 80,
                useNativeDriver: USE_NATIVE_DRIVER
            }),
            Animated.timing(opacity, {
                toValue: 1,
                duration: 200,
                easing: Easing.out(Easing.ease),
                useNativeDriver: USE_NATIVE_DRIVER
            })
        ]).start();
    }, [scale, opacity, reduced]);

    return (
        <Animated.View
            pointerEvents="auto"
            style={[styles.stripe, { transform: [{ scale }], opacity }]}
            accessibilityLiveRegion="polite"
            accessibilityLabel={`Transcribing, adding to ${fieldLabel}`}
        >
            <LinearGradient
                colors={gradients.brand}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.aiOrb}
            >
                <MaterialCommunityIcons name="creation" size={18} color="#fff" />
            </LinearGradient>
            <View style={styles.textCol}>
                <View style={styles.headRow}>
                    <Text style={styles.title}>Transcribing</Text>
                    <ThinkingDots />
                </View>
                <Text style={styles.subtitle}>Adding to {fieldLabel}</Text>
            </View>
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    stripe: {
        width: STRIPE_WIDTH,
        maxWidth: "100%",
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingVertical: 8,
        paddingLeft: 8,
        paddingRight: 14,
        borderRadius: 999,
        backgroundColor: "#fff",
        borderWidth: 1.5,
        borderColor: "rgba(69,210,176,0.4)",
        ...Platform.select({
            ios: {
                shadowColor: "#1F1547",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.18,
                shadowRadius: 14
            },
            android: { elevation: 10 },
            default: {}
        })
    },
    aiOrb: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.3,
                shadowRadius: 12
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    textCol: { flex: 1, minWidth: 0 },
    headRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    title: {
        fontSize: 13,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.2
    },
    subtitle: {
        fontSize: 10,
        fontFamily: fonts.semiBold,
        color: colors.textTertiary,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        marginTop: 1
    },
    dotsRow: { flexDirection: "row", gap: 3, alignItems: "center" },
    dot: { width: 4, height: 4, borderRadius: 2, backgroundColor: colors.gray500 }
});
